import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import { AdditiveBlending, Group, Mesh, MeshBasicMaterial, Vector3 } from 'three';
import { LightningStrike, type RayParameters } from 'three-stdlib';

// three-stdlib's typings don't expose `rayParameters` on the instance (it's
// added at runtime by the constructor). The official three.js demo at
// https://yomboprime.github.io/lightning_strike_demo/webgl_lightningstrike.html
// shows the canonical usage: mutate strike.rayParameters fields each frame,
// then call strike.update(t).
type LightningStrikeWithParams = LightningStrike & { rayParameters: RayParameters };

/**
 * Lightning-spark burst emitter using three.js' built-in `LightningStrike`
 * geometry — jagged forking bolts with proper propagation and vanish
 * animation. Approach mirrors the official three.js example: pool of
 * Mesh<LightningStrike> instances, reused across bursts by mutating their
 * rayParameters (birthTime / deathTime / endpoints).
 *
 * Spawned imperatively (not via JSX) because LightningStrike geometry
 * requires update(t) calls each frame — R3F's declarative JSX doesn't
 * give us per-instance imperative control.
 */

const POOL_SIZE = 6;
const BURST_INTERVAL_MIN = 1.4;
const BURST_INTERVAL_MAX = 2.8;
const STRIKE_LIFE = 0.55;       // seconds — bolt fully expires
const BOLT_LENGTH_MIN = 0.20;
const BOLT_LENGTH_MAX = 0.45;
const FORWARD_BIAS = 0.65;      // 0=fully omnidirectional, 1=pure forward

const SPARK_COLOR = 0xfff088;   // hot yellow-white

interface StrikeSlot {
  strike: LightningStrikeWithParams;
  mesh: Mesh;
  expiresAt: number;
}

export function HeartSparkBurst() {
  const groupRef = useRef<Group>(null);
  const poolRef = useRef<StrikeSlot[]>([]);
  const nextBurstAt = useRef(0);
  const elapsedRef = useRef(0);
  const clock = useThree((s) => s.clock);

  // Build pool once on mount
  useEffect(() => {
    if (!groupRef.current) return;
    const group = groupRef.current;
    const pool: StrikeSlot[] = [];

    for (let i = 0; i < POOL_SIZE; i++) {
      const params = {
        sourceOffset: new Vector3(),
        destOffset: new Vector3(),
        // Small radii — these are SPARKS, not full lightning
        radius0: 0.006,
        radius1: 0.002,
        minRadius: 0.001,
        maxIterations: 4,
        // One-shot bolts (isEternal=false). Birth/death set per spawn.
        isEternal: false,
        birthTime: -1,
        deathTime: -0.9,
        // Animation timing — quick zip
        timeScale: 1.3,
        propagationTimeFactor: 0.25,
        vanishingTimeFactor: 0.75,
        // Branching — moderate forking for spark look
        subrayPeriod: 0.4,
        subrayDutyCycle: 0.55,
        maxSubrayRecursion: 2,
        ramification: 3,
        recursionProbability: 0.45,
        // Path shape
        roughness: 0.92,
        straightness: 0.5,
      };
      const strike = new LightningStrike(params) as LightningStrikeWithParams;
      const mesh = new Mesh(
        strike,
        new MeshBasicMaterial({
          color: SPARK_COLOR,
          transparent: true,
          opacity: 1,
          blending: AdditiveBlending,
          depthWrite: false,
          toneMapped: false,
        }),
      );
      mesh.visible = false;
      mesh.frustumCulled = false;
      group.add(mesh);
      pool.push({ strike, mesh, expiresAt: 0 });
    }
    poolRef.current = pool;
    nextBurstAt.current = clock.getElapsedTime() + BURST_INTERVAL_MIN +
      Math.random() * (BURST_INTERVAL_MAX - BURST_INTERVAL_MIN);

    return () => {
      for (const slot of pool) {
        group.remove(slot.mesh);
        (slot.mesh.material as MeshBasicMaterial).dispose();
        // LightningStrike extends BufferGeometry — dispose to free GPU buffers
        slot.strike.dispose();
      }
      poolRef.current = [];
    };
  }, [clock]);

  useFrame(() => {
    elapsedRef.current = clock.getElapsedTime();
    const t = elapsedRef.current;
    const pool = poolRef.current;
    if (pool.length === 0) return;

    // Trigger burst when timer expires
    if (t >= nextBurstAt.current) {
      nextBurstAt.current = t + BURST_INTERVAL_MIN +
        Math.random() * (BURST_INTERVAL_MAX - BURST_INTERVAL_MIN);
      const spawnCount = 3 + Math.floor(Math.random() * 3);   // 3-5 bolts per burst
      let spawned = 0;
      for (const slot of pool) {
        if (spawned >= spawnCount) break;
        if (slot.expiresAt > t) continue;   // still in use
        // Pick random direction with forward bias (bone-local +Z = forward)
        const fwd = new Vector3(0, 0, 1);
        const random = new Vector3(
          (Math.random() - 0.5) * 2,
          (Math.random() - 0.4) * 1.5,   // slight upward bias
          (Math.random() - 0.5) * 1.5,
        ).normalize();
        const dir = fwd.clone().multiplyScalar(FORWARD_BIAS)
          .addScaledVector(random, 1 - FORWARD_BIAS)
          .normalize();
        const length = BOLT_LENGTH_MIN + Math.random() * (BOLT_LENGTH_MAX - BOLT_LENGTH_MIN);

        slot.strike.rayParameters.sourceOffset!.set(0, 0, 0);
        slot.strike.rayParameters.destOffset!.copy(dir.multiplyScalar(length));
        slot.strike.rayParameters.birthTime = t;
        slot.strike.rayParameters.deathTime = t + STRIKE_LIFE;
        slot.expiresAt = t + STRIKE_LIFE;
        slot.mesh.visible = true;
        spawned++;
      }
    }

    // Animate active strikes; hide expired ones
    for (const slot of pool) {
      if (slot.expiresAt <= t) {
        if (slot.mesh.visible) slot.mesh.visible = false;
        continue;
      }
      slot.strike.update(t);
      // Fade opacity over the last 30% of life
      const lifeT = (slot.expiresAt - t) / STRIKE_LIFE;
      const mat = slot.mesh.material as MeshBasicMaterial;
      mat.opacity = Math.min(1, lifeT / 0.3);
    }
  });

  return <group ref={groupRef} />;
}
